import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { Virtuoso } from 'react-virtuoso';
import SearchInput from '@/components/common/search-input';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import SearchListItem from '@/components/search/search-list-item';
import TagHeaderMenu from '@/components/tags/tag-header-menu';
import useFuseSearch from '@/hooks/search/use-fuse-search';
import useRemoveTagMember from '@/hooks/tags/use-remove-tag-member';
import useTagMembers from '@/hooks/tags/use-tag-members';
import { useTagById, useTagMemberLimit } from '@/hooks/tags/use-tags';
import type { TagMember } from '@/models/user/tag';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { addTranslateToast } from '@/stores/toast';

export default function TagByIdRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tagId } = useTypedParams(ROUTES.CONTACT.TAGS.BY_ID);
  const { data: currentTag } = useTagById(tagId);
  const { data: tagMemberLimit = 1 } = useTagMemberLimit();
  const { tagMembers } = useTagMembers(tagId);
  const [keyword, setKeyword] = useState<string>('');
  const { mutateAsync: removeTagMember } = useRemoveTagMember(tagId);

  const filteredMembers = useFuseSearch({
    data: tagMembers,
    keyword,
    keys: ['NickName', 'RemarkName'],
    initialReturnAll: true,
  });

  const handleNavigateToAddMembers = () => {
    if (tagMembers.length >= tagMemberLimit) {
      addTranslateToast({
        type: 'warning',
        message: `conversation.forward.max-selected|count=${tagMemberLimit}`,
      });
    } else {
      navigate(ROUTES.CONTACT.TAGS.BY_ID.$.ADD_MEMBER.$path({ relative: true }));
    }
  };

  const handleRemoveTagMember = (member: TagMember) =>
    makePrompt({
      key: 'remove-tag-member',
      type: 'dialog',
      title: t('contact.tags.remove-tag-member.title'),
      content: t('contact.tags.remove-tag-member.confirm-message', { nickName: member.NickName }),
      promise: () => removeTagMember(member.MemberId),
    });

  return (
    <PageLayout
      title={currentTag ? `${currentTag.TagName}(${currentTag.TagCount})` : t('contact.tags.title')}
      className='pb-safe-offset-4'
      headerConfig={{
        actions: <TagHeaderMenu />,
        navigateBack: true,
        headerLabel: currentTag && (
          <div className='text-2xl font-bold flex-1 flex min-w-0  mode-embedded:text-center mode-embedded:text-[1rem]'>
            <span className='truncate'>{currentTag.TagName}</span>
            <span>({currentTag.TagCount})</span>
          </div>
        ),
        elementAfterNav: (
          <SearchInput
            className='mx-5'
            keyword={keyword}
            setKeyword={setKeyword}
            clearKeyword={() => setKeyword('')}
          />
        ),
      }}>
      <Virtuoso
        className='flex flex-1 flex-col items-stretch'
        data={filteredMembers}
        itemContent={(_, member) => (
          <SearchListItem
            avatarImageUrl={member.HeadImg}
            title={member.RemarkName || member.NickName}
            highlight={keyword}
            suffix={
              <button
                type='button'
                className='btn btn-primary btn-sm'
                onClick={() => handleRemoveTagMember(member)}>
                {t('common.remove')}
              </button>
            }
          />
        )}
      />

      <button
        type='button'
        className='btn btn-primary btn-md mx-5 desktop:mb-4'
        onClick={handleNavigateToAddMembers}>
        {t('common.add')}
      </button>
    </PageLayout>
  );
}
